import {
  Component,
  computed,
  HostBinding,
  input,
  OnInit,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  InteractionLogForm,
  InteractionLogModuleConfiguration,
  ListItem,
  Octopus,
  User,
} from '@maximizer/core/shared/domain';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { StringValidator } from '@maximizer/core/shared/ui/validators';
import { InteractionService } from '@maximizer/outlook/interaction-log/data-access';
import {
  AbEntryService,
  LeadService,
  NavigationService,
  OutlookSessionService,
  OutlookStore,
} from '@maximizer/outlook/shared/data-access';
import {
  AbEntryLastContactDatePreference,
  EmailEntryType,
  EntryType,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DATEINPUTS } from '@progress/kendo-angular-dateinputs';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { catchError, finalize, forkJoin, of } from 'rxjs';

@Component({
  selector: 'maximizer-call-log',
  templateUrl: './call-log.component.html',
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DATEINPUTS,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class CallLogComponent implements OnInit {
  @HostBinding('class.max-outlook') hostClass = true;

  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  type = input.required<EmailEntryType>();
  id = input.required<string>();

  leadKey = computed(() => {
    return this.type() === EntryType.Lead ? decodeURI(this.id()) : null;
  });

  abEntryKey = computed(() => {
    return this.type() === EntryType.AbEntry ? decodeURI(this.id()) : null;
  });

  currentId = computed(() => {
    return this.leadKey() ?? this.abEntryKey() ?? '';
  });

  private currentUser?: User;

  form?: FormGroupWrapper<InteractionLogForm>;
  configuration?: InteractionLogModuleConfiguration;
  loading = true;
  canSave = false;
  now = new Date();
  parentKey?: string;
  abEntryType?: string;
  lastContactDate?: Date;

  noneValue: ListItem<string> = {
    id: '-1',
    name: this.translate.instant('outlook.forms.none'),
  };

  direction: ListItem<number>[] = [
    {
      id: 1,
      name: this.translate.instant('outlook.callLog.directionIncoming'),
    },
    {
      id: 2,
      name: this.translate.instant('outlook.callLog.directionOutgoing'),
    },
  ];

  constructor(
    private readonly abEntryService: AbEntryService,
    private readonly interactionService: InteractionService,
    private readonly session: OutlookSessionService,
    private readonly leadService: LeadService,
    private readonly translate: TranslateService,
    private readonly navigationService: NavigationService,
    public outlookStore: OutlookStore,
  ) {}

  ngOnInit(): void {
    this.setForm();

    this.configuration = {
      callLogOptions: {
        categories: [this.noneValue],
        results: [this.noneValue],
      },
      phones: [this.noneValue],
    };

    this.form?.patch({
      phone: this.noneValue.name,
      category: this.noneValue.id,
      result: this.noneValue.id,
    });

    if (this.leadKey() || this.abEntryKey()) this.loadData();
  }

  transformToNullWhenNone(input: string | null, prop: string): string | null {
    if (prop == 'id') {
      if (input == this.noneValue.id) return null;
    } else if (input == this.noneValue.name) {
      return null;
    }
    return input;
  }

  save(): void {
    if (!this.form) return;

    const phoneField = this.form.value.phone ?? this.noneValue.name;
    const categoryField = this.form.value.category ?? this.noneValue.id;
    const resultField = this.form.value.result ?? this.noneValue.id;

    this.form.patch({
      user: this.currentUser?.key ?? '$CURRENT_USER()',
      abEntryKey: this.abEntryKey(),
      leadKey: this.leadKey(),
      phone: this.transformToNullWhenNone(this.form.value.phone, 'name'),
      category: this.transformToNullWhenNone(this.form.value.category, 'id'),
      result: this.transformToNullWhenNone(this.form.value.result, 'id'),
    });

    if (this.form.value.startDate && this.form.value.endDate) {
      const endTime = this.form.value.endDate.getTime();
      const startTime = this.form.value.startDate.getTime();
      this.form.value.duration = endTime - startTime;
    }

    const validation = this.form.validate();

    if (!validation.valid) return;

    this.loading = true;
    this.interactionService
      .save(this.form.value)
      .pipe(
        catchError(async () => {
          this.onSaveError(phoneField, categoryField, resultField);
          this.notification().show('create', 'error');
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((result) => {
        if (result == null) return;

        const isAbEntry = this.type() === EntryType.AbEntry;
        const formDate = this.form?.value.endDate ?? new Date();

        if (isAbEntry) {
          this.updateLastContactDate(formDate);
        } else {
          this.completeSuccessCallLogCreation();
        }
      });
  }

  cancel(): void {
    this.navigationService.navigateBack();
  }

  onSaveError(phone: string, category: string, result: string) {
    this.form?.patch({
      phone: phone,
      category: category,
      result: result,
    });
  }

  onStartChange(): void {
    if (!this.form) return;
    this.now = this.form.controls.startDate.value;
  }

  private setForm(): void {
    this.form = FormWrapperBuilder.group<InteractionLogForm>(
      {
        key: null,
        user: null,
        phone: '-1',
        category: '-1',
        direction: [2, [Validators.required]],
        subject: [
          null,
          {
            validators: Validators.compose([
              Validators.required,
              Validators.minLength(1),
              StringValidator.NotEmpty(),
              Validators.maxLength(1024),
            ]),
          },
        ],
        callLog: null,
        result: '-1',
        startDate: [this.now, [Validators.required]],
        endDate: [
          new Date(this.now.getTime() + 30 * 60 * 1000),
          [Validators.required],
        ],
        abEntryKey: null,
        leadKey: null,
        type: Octopus.InteractionCode.CALL,
        duration: null,
      },

      this.save,
      () => {
        this.form?.control.setErrors(null);
        if (this.form?.valid) {
          this.canSave = true;
        } else {
          this.canSave = false;
        }
      },
    );
  }

  private loadData(): void {
    const request = {
      interactionOptions: this.interactionService.getFieldOption(),
      currentUser: this.session.getCurrentUser(),
      lead:
        this.type() === EntryType.Lead
          ? this.leadService.getByKey(this.leadKey() ?? '')
          : of(null),
      abEntry:
        this.type() === EntryType.AbEntry
          ? this.abEntryService.getByKey(this.abEntryKey() ?? '')
          : of(null),
    };

    forkJoin(request)
      .pipe(
        catchError((error) => {
          console.error('Error loading data:', error);
          this.notification().show('load', 'error');
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((data) => {
        if (!data) return;

        this.currentUser = data.currentUser;
        const { categories, results } = data.interactionOptions;
        const phoneList: ListItem<string>[] = [this.noneValue];
        categories.unshift(this.noneValue);
        results.unshift(this.noneValue);

        const entity = data.lead || data.abEntry;
        if (entity) {
          this.validateAndAddPhone(entity.phone1, phoneList);
          this.validateAndAddPhone(entity.phone2, phoneList);

          if ('phone3' in entity) {
            this.validateAndAddPhone(entity.phone3, phoneList);
          }
        }

        this.configuration = {
          callLogOptions: {
            categories: categories ?? [this.noneValue],
            results: results ?? [this.noneValue],
          },
          phones: phoneList,
        };

        this.form?.patch({
          phone: this.noneValue.name,
          category: this.noneValue.id,
          result: this.noneValue.id,
        });

        if (data.abEntry) {
          this.handleAbEntryProperties(data.abEntry);
        }
      });
  }

  private validateAndAddPhone(
    phone: string | undefined,
    phoneList: ListItem<string>[],
  ): void {
    if (phone && phone.length > 1)
      phoneList.push({
        id: phoneList.length.toString(),
        name: phone.trim(),
      });
  }

  private handleAbEntryProperties(abEntry: any): void {
    this.abEntryType = abEntry.type;
    this.lastContactDate = abEntry.lastContactDateValue
      ? new Date(abEntry.lastContactDateValue)
      : undefined;

    if (abEntry.type === EntryType.Contact && abEntry.parentKey) {
      this.parentKey = abEntry.parentKey;
    }
  }

  private updateLastContactDate(date: Date): void {
    this.loading = true;

    this.abEntryService
      .updateLastContactDateWithPreferences(
        this.currentId(),
        this.type(),
        AbEntryLastContactDatePreference.InteractionLog,
        date,
        this.abEntryType,
        this.parentKey,
      )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.completeSuccessCallLogCreation();
        }),
      )
      .subscribe();
  }

  private completeSuccessCallLogCreation(): void {
    this.notification().show('create', 'success');
    this.navigationService.navigateBack();
  }
}
